import { useState } from 'react'
import type { ReactNode } from 'react'
import './NavSidebar.css'

export type NavView =
  | 'cockpit'
  | 'eligibility'
  | 'doctors'
  | 'recentChecks'
  | 'recoveryLoop'
  | 'rpaJobs'
  | 'analytics'
  | 'benefitsCapture'
  | 'clinics'
  | 'users'
  | 'integrations'

interface NavSidebarProps {
  view: NavView
  onNavigate: (view: NavView) => void
  isManagerOrAdmin: boolean
  isAdmin: boolean
  demoEnabled: boolean
  /** Org master switch — when off, the Recovery Loop nav item is hidden. */
  smsRecoveryEnabled: boolean
  /** Staff-set page switch — when on, every role of the practice sees the
   *  Benefits Capture item (the page is the whole practice's scoreboard). */
  benefitsCaptureEnabled: boolean
  userEmail: string | null
  userClinicName: string | null
  /**
   * Admin working-clinic picker (see utils/workingClinic.ts). Which clinic the
   * admin's clinic-scoped actions act on — manual check, CSV upload, Doctors.
   * Rendered only for admins with more than one active clinic;
   * with exactly one there's nothing to choose, so it shows as plain text like
   * a non-admin's assigned clinic. Empty list = still loading.
   */
  workingClinics?: readonly { id: string; name: string }[]
  workingClinicId?: string | null
  onWorkingClinicChange?: (clinicId: string) => void
  onSignOut: () => void
}

const COLLAPSED_KEY = 'navSidebarCollapsed'

const ICONS: Record<string, ReactNode> = {
  cockpit: <svg viewBox="0 0 24 24"><path d="M3 13h8V3H3zM13 21h8V8h-8zM13 3v3h8V3zM3 21h8v-4H3z" /></svg>,
  eligibility: <svg viewBox="0 0 24 24"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg>,
  recentChecks: <svg viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h10" /></svg>,
  recoveryLoop: <svg viewBox="0 0 24 24"><path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5c-1.7 0-3.3-.5-4.6-1.3L3 20l1.3-4.9A8.5 8.5 0 1 1 21 11.5z" /></svg>,
  rpaJobs: <svg viewBox="0 0 24 24"><path d="M4 4v6h6M20 20v-6h-6" /><path d="M20 9A8 8 0 0 0 6 5M4 15a8 8 0 0 0 14 4" /></svg>,
  analytics: <svg viewBox="0 0 24 24"><path d="M3 3v18h18" /><path d="M7 14l3-3 3 3 5-6" /></svg>,
  benefitsCapture: <svg viewBox="0 0 24 24"><path d="M3 20h18" /><rect x="5" y="11" width="3" height="7" rx="0.5" /><rect x="10.5" y="7" width="3" height="11" rx="0.5" /><rect x="16" y="4" width="3" height="14" rx="0.5" /></svg>,
  doctors: <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 20a8 8 0 0 1 16 0" /></svg>,
  users: <svg viewBox="0 0 24 24"><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M16 6a3 3 0 0 1 0 6M21 20a6 6 0 0 0-5-5.9" /></svg>,
  clinics: <svg viewBox="0 0 24 24"><path d="M3 21V8l9-5 9 5v13" /><path d="M9 21v-6h6v6" /></svg>,
  integrations: <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><path d="M14 17h7M17.5 14v7" /></svg>,
}

function emailInitials(email: string | null): string {
  if (!email) return '?'
  const parts = email.split('@')[0].split(/[^a-zA-Z0-9]+/).filter(Boolean)
  return parts.slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?'
}

export default function NavSidebar({
  view,
  onNavigate,
  isManagerOrAdmin,
  isAdmin,
  demoEnabled,
  smsRecoveryEnabled,
  benefitsCaptureEnabled,
  userEmail,
  userClinicName,
  workingClinics = [],
  workingClinicId = null,
  onWorkingClinicChange,
  onSignOut,
}: NavSidebarProps) {
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(COLLAPSED_KEY)
    if (stored !== null) return stored === '1'
    return window.innerWidth <= 768
  })

  function toggleCollapsed() {
    setCollapsed(prev => {
      localStorage.setItem(COLLAPSED_KEY, prev ? '0' : '1')
      return !prev
    })
  }

  const renderItem = (target: NavView, label: string) => (
    <button
      className={`nav-side__item${view === target ? ' nav-side__item--active' : ''}`}
      onClick={() => onNavigate(target)}
      // The collapsed-state fly-out tip is position: fixed so the item list's
      // scroll clip can't swallow it — which means CSS alone can't place it
      // beside a scrolled item (static position ignores ancestor scroll).
      // Stamp the item's live midline at hover time; the CSS reads --tip-y.
      onMouseEnter={e => {
        const r = e.currentTarget.getBoundingClientRect()
        e.currentTarget.style.setProperty('--tip-y', `${r.top + r.height / 2}px`)
      }}
    >
      {ICONS[target]}
      <span className="nav-side__item-txt">{label}</span>
      <span className="nav-side__item-tip">{label}</span>
    </button>
  )

  return (
    <aside className={`nav-side${collapsed ? ' nav-side--collapsed' : ''}`}>
      <div className="nav-side__brand">
        <div className="nav-side__logo">V</div>
        <span className="nav-side__name">VitalRev</span>
        <button
          className="nav-side__toggle"
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand' : 'Collapse'}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>
      <nav className="nav-side__nav">
        <div className="nav-side__group">
          <div className="nav-side__group-label">Operations</div>
          {renderItem('cockpit', 'Daily Cockpit')}
          {renderItem('eligibility', 'Eligibility')}
          {renderItem('recentChecks', 'Recent Checks')}
          {smsRecoveryEnabled && renderItem('recoveryLoop', 'Recovery Loop')}
          {isManagerOrAdmin && renderItem('rpaJobs', 'Data Syncs')}
          {isManagerOrAdmin && !demoEnabled && renderItem('analytics', 'Analytics')}
          {benefitsCaptureEnabled && renderItem('benefitsCapture', 'Benefits Capture')}
        </div>
        {isManagerOrAdmin && (
          <div className="nav-side__group">
            <div className="nav-side__group-label">Admin</div>
            {renderItem('doctors', 'Doctors')}
            {isAdmin && renderItem('users', 'Users')}
            {isAdmin && renderItem('clinics', 'Clinics')}
            {isAdmin && renderItem('integrations', 'Integrations')}
          </div>
        )}
      </nav>
      <div className="nav-side__user">
        <div className="nav-side__avatar">{emailInitials(userEmail)}</div>
        <div className="nav-side__user-meta">
          <div className="nav-side__user-name">{userEmail}</div>
          {workingClinics.length > 1 && onWorkingClinicChange ? (
            <select
              className="nav-side__clinic-picker"
              value={workingClinicId ?? ''}
              onChange={e => onWorkingClinicChange(e.target.value)}
              title="Clinic your actions apply to"
              aria-label="Working clinic"
            >
              {workingClinics.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          ) : (
            (userClinicName ?? workingClinics[0]?.name) &&
              <div className="nav-side__user-sub">{userClinicName ?? workingClinics[0]?.name}</div>
          )}
        </div>
        <button className="nav-side__signout" onClick={onSignOut} title="Sign out" aria-label="Sign out">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
        </button>
      </div>
    </aside>
  )
}
